// src/pages/ViewerPage.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ViewerHeader from '../components/viewer/ViewerHeader';
import InfoCard from '../components/viewer/InfoCard';
import ViewerStats from '../components/viewer/ViewerStats';
import CheckBreakdown from '../components/viewer/CheckBreakdown';
import RecommendationBox from '../components/viewer/RecommendationBox';
import ViewerTable from '../components/viewer/ViewerTable';
import ApprovalStatusBanner from '../components/viewer/ApprovalStatusBanner';
import ExpiryRenewal from '../components/viewer/ExpiryRenewal';
import Correspondence from '../components/viewer/Correspondence';
import ContactFooter from '../components/ContactFooter';
import { getValidationResults } from '../services/api';
import '../styles/Viewer.css';

// Auto-refresh interval for pending approvals (30 seconds)
const REFRESH_INTERVAL = 30000;

// Statuses that are final — no need to keep refreshing
const TERMINAL_STATUSES = ['APPROVED', 'DENIED', 'CANCELLED', 'EXPIRED'];

const ViewerPage = ({ userAlias }) => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const refreshIntervalRef = useRef(null);

  const requestId = searchParams.get('id') || searchParams.get('requestId');

  // Fetch data function (reusable for initial load and refresh)
  const fetchData = useCallback(async (isRefresh = false) => {
    if (!requestId) {
      setError('No Request ID provided. Please use a valid link.');
      setLoading(false);
      return;
    }

    if (isRefresh) {
      setIsRefreshing(true);
    }

    try {
      const result = await getValidationResults(requestId);
      setData(result);
      setLastRefreshed(new Date());

      if (!isRefresh) {
        document.title = `UFS Results - ${requestId}`;
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (!isRefresh) {
        setError(`Failed to load validation results: ${err.message}`);
      }
      // On refresh errors, keep showing old data — don't set error
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [requestId]);

  // Initial load
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Auto-refresh for pending approvals
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    const approvalStatus = data?.approvalStatus;

    // Only auto-refresh if there's an approval status and it's not terminal
    if (approvalStatus && !TERMINAL_STATUSES.includes(approvalStatus)) {
      console.log(`[Viewer] Auto-refresh enabled for status: ${approvalStatus}`);

      refreshIntervalRef.current = setInterval(() => {
        console.log('[Viewer] Auto-refreshing...');
        fetchData(true);
      }, REFRESH_INTERVAL);
    }

    // Cleanup on unmount or status change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [data?.approvalStatus, fetchData]);

  // Manual refresh handler
  const handleManualRefresh = () => {
    if (!isRefreshing) {
      fetchData(true);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="viewer-container">
        <ViewerHeader requestId={requestId} />
        <div className="viewer-content">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading validation results...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="viewer-container">
        <ViewerHeader requestId={requestId} />
        <div className="viewer-content">
          <div className="error-box">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <p style={{ marginTop: '16px', fontSize: '14px' }}>
              Please check the Request ID and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const hasApproval = !!data?.approvalStatus;
  // Bindle and Ruleset requests have NO per-user validation — they capture form
  // fields only. For these we render a detail view instead of the validation UI.
  const isBindle = data?.requestType === 'bindle_access' || data?.team === 'Bindle Access';
  const isRuleset = data?.requestType === 'ruleset_access' || data?.team === 'Ruleset Access';
  const isFormOnly = isBindle || isRuleset;

  return (
    <div className="viewer-container" style={{ paddingBottom: '80px' }}>
      <ViewerHeader requestId={requestId} />
      <div className="viewer-content">

        {/* Approval Status Banner — shows only for Unlisted Use Case requests */}
        {hasApproval && (
          <>
            <ApprovalStatusBanner data={data} isRefreshing={isRefreshing} />

            {/* Refresh bar */}
            <div className="viewer-refresh-bar">
              <button
                className="viewer-refresh-btn"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh Status'}
              </button>
              {lastRefreshed && (
                <span className="viewer-refresh-time">
                  Last updated: {lastRefreshed.toLocaleTimeString()}
                </span>
              )}
            </div>
          </>
        )}

        {/* Access expiry + re-baseline CTA — renders only for APPROVED requests
            (component self-guards on status/daysLeft/renewalRequestId). */}
        <ExpiryRenewal data={data} requestId={requestId} />

        {isFormOnly ? (
          /* ── Bindle / Ruleset: form-field detail view (no validation) ── */
          <FormDetailView data={data} isBindle={isBindle} />
        ) : (
          /* ── Access requests: full validation view ── */
          <>
            <InfoCard data={data} />
            <ViewerStats data={data} />
            <CheckBreakdown data={data} />
            <RecommendationBox data={data} />
            <ViewerTable tableData={data.tableData} requestId={requestId} riskType={data.riskTypeGroup} />
          </>
        )}

        {/* Two-way correspondence — surfaces approvals.amazon.com conversation inline.
            Shown for any request that reached approvals (has an approvalId) or already
            has comments. Posting is gated to the requester (backend + UI). */}
        <Correspondence
          requestId={requestId}
          userAlias={userAlias}
          approvalId={data?.approvalId}
        />
      </div>
      <ContactFooter />
    </div>
  );
};

// Detail view for form-only requests (Bindle / Ruleset) — shows the captured
// fields rather than user-validation results (which don't apply to these flows).
const FormDetailView = ({ data, isBindle }) => {
  const rows = isBindle
    ? [
        { label: 'Request For', value: data.bindleRequestType },
        { label: 'Permission Type', value: data.bindlePermissionType },
        { label: 'Use Case', value: data.useCase, wide: true },
        { label: 'Paragon Actions', value: data.paragonActions, wide: true },
        { label: 'POSIX Account', value: data.posixAccount },
        { label: 'Safer AI Check', value: data.saferAICheck },
        { label: 'Safer AI ID', value: data.saferAIId },
      ]
    : [
        { label: 'Tools Requested', value: data.toolsRequested },
        { label: 'Rule Detail', value: data.ruleDetail, wide: true },
        { label: 'Use Case', value: data.useCase, wide: true },
        { label: 'User Logins', value: data.userList, wide: true },
      ];

  const permission = (data.selectedPermissionNames && data.selectedPermissionNames[0]) || data.useCaseValue || '—';
  const title = isBindle ? '📦 Bindle Access Request' : '📐 Ruleset Access Request';
  // Only show rows that actually have a value (older records may lack some fields).
  const visibleRows = rows.filter(r => r.value && String(r.value).trim());

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', marginTop: '12px' }}>
      <div style={{ fontSize: '16px', fontWeight: '800', color: '#111', marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
        Requester: <strong>{data.requesterAlias || '—'}</strong>
        {'  ·  '}Permission: <strong>{permission}</strong>
      </div>

      {visibleRows.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
          No request details were captured for this request.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          {visibleRows.map((r, i) => (
            <div key={i} style={{ gridColumn: r.wide ? '1 / -1' : 'auto' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '3px' }}>{r.label}</div>
              <div style={{ fontSize: '14px', color: '#111', whiteSpace: 'pre-wrap' }}>{r.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewerPage;